import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Avatar, Skeleton, Stack, Button, Paper } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { PieChart } from '@mui/x-charts/PieChart';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PieChartIcon from '@mui/icons-material/PieChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';

import useAuthStore from '../store/useAuthStore';
import { getGlobalPerformance } from '../services/adminService';

const ALL_MENU_ITEMS = [
  {
    title: 'Manajemen Unit',
    description: 'Data MWC & Ranting',
    path: '/admin/units',
    icon: <AccountTreeIcon fontSize="large" />,
    color: '#0288d1', 
    roles: ['MANAJERIAL']
  },
  {
    title: 'Manajemen Pengguna',
    description: 'Akses & Role',
    path: '/admin/users',
    icon: <PeopleIcon fontSize="large" />,
    color: '#7b1fa2', 
    roles: ['MANAJERIAL']
  },
  {
    title: 'Kinerja Global',
    description: 'Laporan Lengkap',
    path: '/admin/global-performance',
    icon: <AssessmentIcon fontSize="large" />,
    color: '#d32f2f', 
    roles: ['MANAJERIAL']
  },
  {
    title: 'Transaksi Lazisnu',
    description: 'Input Pusat',
    path: '/admin/transactions',
    icon: <ReceiptIcon fontSize="large" />,
    color: '#2e7d32', 
    roles: ['MANAJERIAL', 'LAPANGAN']
  },
];

const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      p: 3, 
      height: '100%', 
      minHeight: 100, // Menambahkan minHeight untuk konsistensi
      justifyContent: 'center', // Pusatkan vertikal
      flexDirection: 'column' // Agar Avatar dan Box teks bisa ditumpuk
    }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', mb: 1 }}>
            <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
                {icon}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="medium" noWrap title={title}>
                    {title}
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
                    {loading ? <Skeleton width={120} /> : value}
                </Typography>
            </Box>
        </Stack>
    </CardContent>
  </Card>
);

const LazisnuDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pieData, setPieData] = useState([]); // This will now hold category data
  const [loading, setLoading] = useState(true);
  
  const isLapangan = user?.jabatan === 'LAPANGAN';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const today = new Date();
        const data = await getGlobalPerformance(today.getMonth() + 1, today.getFullYear());
        
        let totalPendapatan = 0;
        let totalBeban = 0;
        let totalTransaksi = 0;
        
        // Aggregate MWC/Ranting performance for overall stats
        if (data.performance) {
            data.performance.forEach(unit => {
                totalPendapatan += unit.totalPendapatan;
                totalBeban += unit.totalBeban;
                totalTransaksi += unit.totalTransactions;
            });
        }

        setStats({
            pendapatan: totalPendapatan,
            beban: totalBeban,
            transaksi: totalTransaksi,
            saldo: totalPendapatan - totalBeban
        });

        // NEW: Aggregate by Category for the Pie Chart
        const categoryTotals = {};
        if (data.transactions) {
            data.transactions.forEach(tx => {
                if (tx.jenis === 'PEMASUKAN') {
                    const catName = tx.kategori?.nama || 'Tanpa Kategori';
                    const amount = parseFloat(tx.jumlah);
                    
                    if (!categoryTotals[catName]) categoryTotals[catName] = 0;
                    categoryTotals[catName] += amount;
                }
            });
        }

        const sortedCategoryPieData = Object.entries(categoryTotals)
            .sort(([, a], [, b]) => b - a)
            .map(([label, value], index) => ({
                id: index,
                value,
                label,
                color: `hsl(${150 + (index * 40)}, 60%, ${40 + (index * 10)}%)` // Greenish/Varied hues
            }));
        
        setPieData(sortedCategoryPieData);

      } catch (error) {
        console.error("Gagal memuat statistik dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const filteredMenuItems = ALL_MENU_ITEMS.filter(item => {
      if (isLapangan) return item.roles.includes('LAPANGAN');
      return item.roles.includes('MANAJERIAL');
  });

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box sx={{ mb: {xs: 2, md: 0} }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                    Dashboard Lazisnu
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isLapangan 
                    ? `Halo ${user?.username}, siap untuk mencatat transaksi hari ini?` 
                    : `Selamat Datang, ${user?.username || 'Admin'}. Berikut ringkasan kinerja bulan ini.`}
                </Typography>
            </Box>

            {/* Priority Button for Lapangan */}
            {isLapangan && (
                <Button 
                    variant="contained" 
                    color="success" 
                    size="large" 
                    startIcon={<AddIcon />} 
                    onClick={() => navigate('/admin/transactions')}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold', boxShadow: 3 }}
                >
                    Input Transaksi Baru
                </Button>
            )}
        </Paper>

        {/* HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Pemasukan Bulan Ini" value={stats ? `Rp${stats.pendapatan.toLocaleString('id-ID')}` : 'Rp0'} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Pengeluaran Bulan Ini" value={stats ? `Rp${stats.beban.toLocaleString('id-ID')}` : 'Rp0'} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Surplus/Defisit" value={stats ? `Rp${stats.saldo.toLocaleString('id-ID')}` : 'Rp0'} icon={<AccountBalanceWalletIcon fontSize="large" />} color="#1976d2" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Total Transaksi" value={stats ? stats.transaksi.toLocaleString('id-ID') : '0'} icon={<SwapHorizIcon fontSize="large" />} color="#ed6c02" loading={loading} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
            {/* LEFT COLUMN: Composition Chart (MD=8 to match MWC style) */}
            <Grid item xs={12} md={8}>
                <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 3 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <PieChartIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Komposisi Pemasukan Global</Typography>
                        </Stack>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ height: 200, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    {loading ? <Skeleton variant="circular" width={180} height={180} /> : 
                                     (stats?.pendapatan === 0 ? <Typography color="text.secondary">Belum ada data.</Typography> :
                                        <PieChart series={[{ data: pieData, innerRadius: 40, outerRadius: 90, paddingAngle: 2, cornerRadius: 4 }]} width={250} height={200} slotProps={{ legend: { hidden: true } }} />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {!loading && stats?.pendapatan > 0 && (
                                    <Stack spacing={2}>
                                        {pieData.map((d) => (
                                            <Box key={d.id} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                                    <Box sx={{ width: 12, height: 12, bgcolor: d.color, borderRadius: '50%' }} />
                                                    <Typography variant="body2" color="text.secondary">{d.label}</Typography>
                                                </Stack>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {((d.value / stats.pendapatan) * 100).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Rp{d.value.toLocaleString('id-ID')}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT COLUMN: Quick Menu (MD=4) */}
            <Grid item xs={12} md={4}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DashboardIcon sx={{ mr: 1 }} /> Menu Utama
                        </Typography>
                        <Stack spacing={2}>
                            {filteredMenuItems.map((item) => (
                                <Card key={item.title} sx={{ transition: '0.2s', '&:hover': { transform: 'translateX(4px)', boxShadow: 4, borderColor: item.color }, borderRadius: 2, borderLeft: `6px solid ${item.color}` }}>
                                    <CardActionArea component={Link} to={item.path} sx={{ p: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Avatar sx={{ bgcolor: `${item.color}15`, color: item.color }}>
                                                {item.icon}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>
                                                <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                                            </Box>
                                        </Stack>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Stack>

                        {/* Supervisi Box (Only for Managerial) */}
                        {!isLapangan && (
                            <Box sx={{ mt: 4, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px dashed #2196f3' }}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <AssessmentIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">Status MWC</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Pantau laporan bulanan dari seluruh MWC untuk memastikan konsolidasi data tepat waktu.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default LazisnuDashboard;